package org.vreo.devicehub.navigation.presentation

import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.Logout
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.lifecycle.viewmodel.navigation3.rememberViewModelStoreNavEntryDecorator
import androidx.navigation3.runtime.entryProvider
import androidx.navigation3.runtime.rememberNavBackStack
import androidx.navigation3.runtime.rememberSaveableStateHolderNavEntryDecorator
import androidx.navigation3.ui.NavDisplay
import org.jetbrains.compose.resources.stringResource

import org.vreo.devicehub.features.core.domain.device.device.DeviceAuthentication
import org.vreo.devicehub.features.device.presentation.addDevice.AddDeviceScreenRoot
import org.vreo.devicehub.features.device.presentation.addDevice.AddDeviceViewModel
import org.vreo.devicehub.features.device.presentation.deviceList.DeviceListScreenRoot
import org.vreo.devicehub.features.device.presentation.deviceList.DeviceListViewModel
import org.vreo.devicehub.features.device.presentation.deviceManagement.mainDeviceNavigation.DeviceManagementScreenRoot
import org.vreo.devicehub.features.device.presentation.deviceManagement.messages.MessageListScreenRoot
import org.vreo.devicehub.features.device.presentation.deviceManagement.messages.MessageListViewModel
import org.vreo.devicehub.features.device.presentation.registerDevice.RegisterDeviceScreenRoot
import org.vreo.devicehub.features.device.presentation.registerDevice.RegisterDeviceViewModel
import org.vreo.devicehub.features.roi.presentation.RoiScreenRoot
import org.vreo.devicehub.features.roi.presentation.RoiViewModel
import org.vreo.devicehub.features.settings.presentation.mainSettings.MainSettingsRoot
import org.vreo.devicehub.features.settings.presentation.mainSettings.MainSettingsViewModel
import org.vreo.devicehub.features.liveAccess.presentation.LiveAccessActions
import org.vreo.devicehub.features.liveAccess.presentation.LiveAccessScreenRoot
import org.vreo.devicehub.features.liveAccess.presentation.LiveAccessViewModel
import org.vreo.devicehub.features.settings.presentation.settings.SettingsScreenRoot
import org.vreo.devicehub.features.settings.presentation.settings.SettingsViewModel
import org.vreo.devicehub.features.userAuthentication.presentation.login.UserAuthenticationScreenRoot
import org.vreo.devicehub.features.userAuthentication.presentation.register.UserRegistrationScreenRoot
import org.vreo.devicehub.features.userAuthentication.presentation.verification.AccountVerificationScreenRoot
import org.vreo.devicehub.features.userAuthentication.presentation.verification.AccountVerificationViewModel
import org.vreo.devicehub.features.vrmOutput.presentation.VrmOutputScreenRoot
import org.vreo.devicehub.features.vrmOutput.presentation.VrmOutputViewModel
import org.vreo.devicehub.navigation.data.MainNavigationKey
import org.vreo.devicehub.features.userAuthentication.domain.UserRole
import org.vreo.devicehub.di.loadSessionKoinModules
import org.vreo.devicehub.di.unLoadSessionKoinModules

import devicehub.composeapp.generated.resources.Res
import devicehub.composeapp.generated.resources.logout_label
import org.vreo.devicehub.features.deviceDetails.presentation.DeviceDetailsScreenRoot
import org.vreo.devicehub.features.deviceDetails.presentation.DeviceDetailsViewModel


/**
 * Main navigation for the application.
 */
@Composable
fun MainNavigation() {
    val mainBackStack = rememberNavBackStack(MainNavigationKey.config, MainNavigationKey.Login)

    NavDisplay(
        modifier = Modifier.fillMaxSize(),
        backStack = mainBackStack,
        entryDecorators = listOf(
            rememberSaveableStateHolderNavEntryDecorator(),
            rememberViewModelStoreNavEntryDecorator()
        ),
        onBack = {
            mainBackStack.removeLastOrNull()
        },
        entryProvider = entryProvider {
            entry<MainNavigationKey.Login> {
                UserAuthenticationScreenRoot(
                    viewModel = koinViewModel(),
                    onUserSignInClicked = { token, userRole ->
                        loadSessionKoinModules()
                        mainBackStack.add(MainNavigationKey.DeviceList(token, userRole))
                    },
                    onNewUserRegistrationClicked = {
                        mainBackStack.add(MainNavigationKey.NewUserRegistration)
                    }
                )
            }

            entry<MainNavigationKey.NewUserRegistration> {
                UserRegistrationScreenRoot(
                    viewModel = koinViewModel(),
                    onUserRegistrationSuccess = { email, validUntil ->
                        mainBackStack.add(MainNavigationKey.NewUserVerification(email, validUntil))
                    },
                    onBackNavigationClick = {
                        mainBackStack.removeLastOrNull()
                    }
                )
            }

            entry<MainNavigationKey.NewUserVerification> { contentKey ->
                val vmKey = contentKey.validUntil

                val viewModel: AccountVerificationViewModel =
                    koinViewModel(key = "verification_$vmKey") { parametersOf(contentKey.email, contentKey.validUntil) }

                AccountVerificationScreenRoot(
                    viewModel = viewModel,
                    onVerificationSuccess = { token ->
                        loadSessionKoinModules()
                        mainBackStack.add(MainNavigationKey.DeviceList(token, UserRole.USER))
                    },
                    onBackNavigationClick = {
                        mainBackStack.removeLastOrNull()
                    },
                )
            }

            entry<MainNavigationKey.DeviceList> { contentKey ->
                val vmKey = contentKey.authenticationToken.take(10)
                val isAdmin = contentKey.userRole.value >= UserRole.ADMIN.value

                val viewModel: DeviceListViewModel = koinViewModel(
                    key = "devices_$vmKey"
                ) { parametersOf(contentKey.authenticationToken, isAdmin) }

                DeviceListScreenRoot(
                    viewModel = viewModel,
                    onAddDevice = {
                        mainBackStack.add(MainNavigationKey.AddDeviceToAccount(contentKey.authenticationToken))
                    },
                    onRegisterDevice = {
                        mainBackStack.add(MainNavigationKey.RegisterNewDevice(contentKey.authenticationToken))
                    },
                    onDeviceSelect = { device ->
                        mainBackStack.add(
                            MainNavigationKey.DeviceManagement(
                                DeviceAuthentication(
                                    userToken = contentKey.authenticationToken,
                                    deviceId = device.deviceId,
                                    isDeviceActive = device.active
                                ),
                                userRole = contentKey.userRole
                            )
                        )
                    },
                    actions = {
                        OutlinedButton(
                            onClick = {
                                mainBackStack.clear()
                                mainBackStack.add(MainNavigationKey.Login)
                                unLoadSessionKoinModules()
                            }
                        ) {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier.padding(4.dp)
                            ) {
                                Icon(
                                    imageVector = Icons.AutoMirrored.Filled.Logout,
                                    contentDescription = null,
                                    modifier = Modifier.padding(end = 4.dp)
                                )
                                Text(
                                    text = stringResource(Res.string.logout_label),
                                    modifier = Modifier.padding(end = 4.dp)
                                )
                            }
                        }
                    }
                )
            }

            entry<MainNavigationKey.AddDeviceToAccount> { contentKey ->
                val vmKey = contentKey.authenticationToken.take(10)

                val viewModel: AddDeviceViewModel = koinViewModel(
                    key = "addDevice_$vmKey"
                ) { parametersOf(contentKey.authenticationToken) }

                AddDeviceScreenRoot(
                    viewModel = viewModel,
                    onAddUserSuccess = {
                        mainBackStack.removeLastOrNull()
                    },
                    onBackNavigation = {
                        mainBackStack.removeLastOrNull()
                    }
                )
            }

            entry<MainNavigationKey.RegisterNewDevice> { contentKey ->
                val vmKey = contentKey.authenticationToken.take(10)

                val viewModel: RegisterDeviceViewModel = koinViewModel(
                    key = "registerDevice_$vmKey"
                ) { parametersOf(contentKey.authenticationToken) }

                RegisterDeviceScreenRoot(
                    viewModel = viewModel,
                    onRegisterDeviceSuccess = {
                        mainBackStack.removeLastOrNull()
                    },
                    onBackNavigation = {
                        mainBackStack.removeLastOrNull()
                    }
                )
            }

            entry<MainNavigationKey.DeviceManagement> { contentKey ->
                val vmKey = contentKey.authentication.deviceId

                val isAdmin = contentKey.userRole.value >= UserRole.ADMIN.value

                val deviceDetailsViewModel: DeviceDetailsViewModel =
                    koinViewModel(key = "deviceDetails_$vmKey") { parametersOf(contentKey.authentication, isAdmin) }
                val messageListViewModel: MessageListViewModel =
                    koinViewModel(key = "messages_$vmKey") { parametersOf(contentKey.authentication) }
                val mainSettingsViewModel: MainSettingsViewModel =
                    koinViewModel(key = "mainSettings_$vmKey") { parametersOf(contentKey.authentication) }
                val liveViewViewModel: LiveAccessViewModel =
                    koinViewModel(key = "liveView_$vmKey") { parametersOf(contentKey.authentication) }
                val settingsViewModel: SettingsViewModel =
                    koinViewModel(key = "settings_$vmKey") { parametersOf(contentKey.authentication, isAdmin) }
                val roiViewModel: RoiViewModel =
                    koinViewModel(key = "roi_$vmKey") { parametersOf(contentKey.authentication) }
                val outputViewModel: VrmOutputViewModel =
                    koinViewModel(key = "output_$vmKey") { parametersOf(contentKey.authentication) }

                DeviceManagementScreenRoot(
                    viewModel = koinViewModel(),
                    onBackNavigation = {
                        liveViewViewModel.onAction(LiveAccessActions.OnDisconnectClicked)
                        mainBackStack.removeLastOrNull()
                    },
                    detailsView = {
                        DeviceDetailsScreenRoot(viewModel = deviceDetailsViewModel)
                    },
                    messagesView = {
                        MessageListScreenRoot(viewModel = messageListViewModel)
                    },
                    settingsView = {
                        MainSettingsRoot(
                            viewModel = mainSettingsViewModel,
                            liveViewContent = {
                                LiveAccessScreenRoot(liveViewViewModel)
                            },
                            settingsSubView = {
                                SettingsScreenRoot(settingsViewModel)
                            }
                        )
                    },
                    roiView = {
                        RoiScreenRoot(viewModel = roiViewModel)
                    },
                    outputView = {
                        VrmOutputScreenRoot(viewModel = outputViewModel)
                    }
                )


            }
        }
    )
}

